import { computed, effect, Injectable, signal } from '@angular/core';
import {
  FFSLC_REGIONS,
  FfslcRegionDefinition,
  getFfslcRegion,
  getFfslcRegionForAdministrativeRegion,
  getFfslcRegionForDepartment,
} from '../data/ffslc-regions';

const DEFAULT_ACCENT = '#2563EB';
const DEFAULT_DARK_ACCENT = '#93C5FD';

@Injectable({ providedIn: 'root' })
export class RegionalThemeService {
  private readonly storageKey = 'canitrack-ffslc-region';
  private readonly selectedRegionCode = signal<string | null>(this.readSavedRegion());
  private readonly contextRegionCode = signal<string | null>(null);

  readonly regions = FFSLC_REGIONS;
  readonly activeRegion = computed<FfslcRegionDefinition | null>(() => {
    const code = this.contextRegionCode() ?? this.selectedRegionCode();
    return code ? getFfslcRegion(code) : null;
  });
  readonly activeLabel = computed(() => {
    const region = this.activeRegion();
    return region ? `${region.code} · ${region.shortName}` : 'Performance · Données';
  });

  constructor() {
    effect(() => {
      const region = this.activeRegion();
      this.applyCssTheme(region);
    });
  }

  selectRegion(code: string | null): void {
    const region = code ? getFfslcRegion(code) : null;
    this.selectedRegionCode.set(region?.code ?? null);
    if (region) {
      localStorage.setItem(this.storageKey, region.code);
    } else {
      localStorage.removeItem(this.storageKey);
    }
  }

  selectDepartment(departmentCode: string | null | undefined): void {
    this.selectRegion(getFfslcRegionForDepartment(departmentCode)?.code ?? null);
  }

  selectAdministrativeRegion(name: string | null | undefined): void {
    this.selectRegion(getFfslcRegionForAdministrativeRegion(name)?.code ?? null);
  }

  setContextRegion(code: string | null): void {
    this.contextRegionCode.set(code && getFfslcRegion(code) ? code : null);
  }

  setContextDepartment(departmentCode: string | null | undefined): void {
    this.contextRegionCode.set(getFfslcRegionForDepartment(departmentCode)?.code ?? null);
  }

  setContextAdministrativeRegion(name: string | null | undefined): void {
    this.contextRegionCode.set(getFfslcRegionForAdministrativeRegion(name)?.code ?? null);
  }

  clearContext(): void {
    this.contextRegionCode.set(null);
  }

  private readSavedRegion(): string | null {
    if (typeof localStorage === 'undefined') return null;
    const code = localStorage.getItem(this.storageKey);
    return code && getFfslcRegion(code) ? code : null;
  }

  private applyCssTheme(region: FfslcRegionDefinition | null): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.style.setProperty('--region-accent', region?.color ?? DEFAULT_ACCENT);
    root.style.setProperty('--region-accent-dark', region?.darkColor ?? DEFAULT_DARK_ACCENT);
    root.dataset['region'] = region?.code ?? 'default';
  }
}
